<template>
  <div class="task-list">
    <!-- <div class="task-list-header">
      <h3>Task List</h3>
      <div class="header-actions">
        <el-button 
          size="mini" 
          type="primary" 
          @click="refreshTasks"
          :loading="refreshing"
        >
          <i class="el-icon-refresh"></i>
        </el-button>
        <el-button 
          size="mini" 
          type="success" 
          @click="createNewTask"
        >
          <i class="el-icon-plus"></i>
        </el-button>
      </div>
    </div> -->
    
    <div class="task-filters">
      <el-select v-model="statusFilter" placeholder="All Status" size="small" style="width: 110px;">
        <el-option label="All" value=""></el-option>
        <el-option label="Pending" value="pending"></el-option>
        <el-option label="Running" value="in-progress"></el-option>
        <el-option label="Done" value="completed"></el-option>
        <el-option label="Failed" value="failed"></el-option>
      </el-select>
      <el-input
        v-model="searchQuery"
        placeholder="Search..."
        size="small"
        style="width: 120px;"
        prefix-icon="el-icon-search"
        clearable
      ></el-input>
    </div>

    <div class="task-list-content">
      <div v-if="filteredTasks.length === 0" class="no-tasks">
        <i class="el-icon-folder-opened"></i>
        <p>No tasks found</p>
        <el-button size="mini" type="primary" @click="createNewTask">Create First Task</el-button>
      </div>
      
      <div 
        v-for="task in filteredTasks" 
        :key="(task._id && (task._id.$oid || task._id.$id)) || task.id"
        class="task-item"
        :class="{ 
          active: ((task._id && (task._id.$oid || task._id.$id)) || task.id) === selectedTaskId,
          'has-operations': task.operations && task.operations.length > 0,
          'in-context-pot': isInContextPot(task)
        }"
        @click="selectTask(task)"
      >
        <div class="task-row">
          <div class="task-checkbox" @click.stop>
            <el-checkbox 
              :value="isTaskCompleted(task)"
              @change="toggleTaskCompletion(task, $event)"
            ></el-checkbox>
          </div>
          <div class="task-title" :class="{ 'completed': isTaskCompleted(task) }">{{ task.title || 'Untitled Task' }}</div>
          
          <!-- Operations count -->
          <span class="operations-count" v-if="task.operations && task.operations.length > 0">
            <i class="el-icon-s-operation"></i>
            {{ task.operations.length }}
          </span>
          
          <!-- Task time -->
          <span class="task-time">{{ formatDate(task.created_at || task.create_time) }}</span>
          
          <!-- Status tag -->
          <div class="task-status">
            <el-tag 
              :type="getStatusTagType(task.status)" 
              size="mini"
              effect="plain"
            >
              {{ task.status || 'pending' }}
            </el-tag>
          </div>
          
          <!-- Actions -->
          <div class="task-actions">
            <!-- Delegate to AI Button -->
            <el-button
              size="mini"
              type="warning"
              @click.stop="delegateToAI(task)"
              class="delegate-ai-btn"
              title="Delegate task to AI for processing"
            >
              <i class="el-icon-cpu"></i>
              <span class="btn-text">AI</span>
            </el-button>
            
            <el-button 
              size="mini" 
              :type="isInContextPot(task) ? 'success' : 'primary'"
              @click.stop="toggleContextPot(task)"
              class="add-to-context-btn"
              :title="isInContextPot(task) ? 'Remove from Context' : 'Add to Context'"
            >
              <i :class="isInContextPot(task) ? 'el-icon-check' : 'el-icon-plus'"></i>
              <span class="btn-text">{{ isInContextPot(task) ? 'Added' : 'Context' }}</span>
            </el-button>
            
            <!-- Three-dot menu -->
            <el-dropdown 
              trigger="click" 
              @command="handleTaskCommand($event, task)"
              @click.native.stop
            >
              <el-button 
                size="mini" 
                type="text" 
                icon="el-icon-more"
                class="more-actions-btn"
                @click.stop
              />
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item command="delegate">
                  <i class="el-icon-cpu"></i>
                  <span>Delegate to AI</span>
                </el-dropdown-item>
                <el-dropdown-item command="edit">
                  <i class="el-icon-edit"></i>
                  <span>Edit</span>
                </el-dropdown-item>
                <el-dropdown-item command="canvas">
                  <i class="el-icon-s-platform"></i>
                  <span>Open Canvas</span>
                </el-dropdown-item>
                <el-dropdown-item command="delete" divided>
                  <span style="color: #f56c6c;">
                    <i class="el-icon-delete"></i>
                    Delete
                  </span>
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "TaskList",
  props: {
    tasks: {
      type: Array,
      default: () => []
    },
    selectedTaskId: {
      type: [String, Number],
      default: null
    },
    contextPot: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      statusFilter: '',
      searchQuery: '',
      refreshing: false
    };
  },
  computed: {
    filteredTasks() {
      let filtered = this.tasks;
      
      // Filter by status
      if (this.statusFilter) {
        filtered = filtered.filter(task => 
          (task.status || 'pending') === this.statusFilter
        );
      }
      
      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(task => 
          (task.title && task.title.toLowerCase().includes(query)) ||
          (task.description && task.description.toLowerCase().includes(query)) ||
          (task.task_type && task.task_type.toLowerCase().includes(query))
        );
      }
      
      return filtered;
    }
  },
  methods: {
    selectTask(task) {
      this.$emit('task-selected', task);
    },
    
    refreshTasks() {
      this.refreshing = true;
      this.$emit('refresh-tasks');
      setTimeout(() => {
        this.refreshing = false;
      }, 1000);
    },
    
    createNewTask() {
      this.$emit('create-task');
    },
    
    viewTaskDetails(task) {
      this.$emit('view-task', task);
    },
    
    editTask(task) {
      this.$emit('edit-task', task);
    },
    
    openTaskCanvas(task) {
      // Navigate to TaskCanvas page with task information
      this.$router.push({
        name: 'TaskCanvas',
        query: {
          id: (task._id && (task._id.$oid || task._id.$id)) || task.id,
          title: task.title || 'Untitled Task',
          status: task.status || 'pending'
        }
      });
    },
    
    deleteTask(task) {
      this.$confirm(
        `Are you sure you want to delete "${task.title || 'this task'}"?`,
        'Confirm Delete',
        {
          confirmButtonText: 'Delete',
          cancelButtonText: 'Cancel',
          type: 'warning'
        }
      ).then(() => {
        this.$emit('delete-task', task);
      }).catch(() => {
        // User cancelled
      });
    },
    
    getStatusTagType(status) {
      const statusMap = {
        'pending': 'info',
        'in-progress': 'warning',
        'running': 'warning',
        'completed': 'success',
        'failed': 'danger',
        'error': 'danger'
      };
      return statusMap[status] || 'info';
    },
    
    getPriorityClass(priority) {
      const priorityMap = {
        'low': 'priority-low',
        'medium': 'priority-medium',
        'high': 'priority-high',
        'urgent': 'priority-urgent'
      };
      return priorityMap[priority] || 'priority-medium';
    },
    
    formatDate(dateString) {
      if (!dateString) return '';
      
      console.log('formatDate input:', dateString, 'type:', typeof dateString);
      
      let date;
      try {
        // Handle different date formats
        if (typeof dateString === 'string') {
          if (dateString.includes('T')) {
            // ISO date string
            date = new Date(dateString);
          } else if (dateString.match(/^\d{4}-\d{2}-\d{2}/)) {
            // Date only string (YYYY-MM-DD)
            date = new Date(dateString + 'T00:00:00');
          } else if (/^\d+$/.test(dateString)) {
            // Numeric string - could be Unix timestamp
            const num = parseInt(dateString);
            if (num < 10000000000) {
              // Seconds since epoch, convert to milliseconds
              date = new Date(num * 1000);
            } else {
              // Milliseconds since epoch
              date = new Date(num);
            }
          } else {
            // Try standard parsing
            date = new Date(dateString);
          }
        } else if (typeof dateString === 'number') {
          // Numeric value
          if (dateString < 10000000000) {
            // Seconds since epoch, convert to milliseconds
            date = new Date(dateString * 1000);
          } else {
            // Milliseconds since epoch
            date = new Date(dateString);
          }
        } else {
          date = new Date(dateString);
        }
        
        console.log('Parsed date:', date, 'timestamp:', date.getTime(), 'isValid:', !isNaN(date.getTime()));
        
        // Check if date is valid
        if (isNaN(date.getTime())) {
          console.warn('Invalid date result:', dateString, '->', date);
          return 'Invalid date';
        }
        
        // Get current date in local timezone (start of day)
        const now = new Date();
        const nowStartOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        // Get parsed date in local timezone (start of day)
        const dateStartOfDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        
        // Calculate difference in days
        const diffTime = nowStartOfDay.getTime() - dateStartOfDay.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        console.log('Date comparison:', { 
          now: now.toISOString(), 
          nowStartOfDay: nowStartOfDay.toISOString(),
          parsed: date.toISOString(), 
          dateStartOfDay: dateStartOfDay.toISOString(),
          diffTime,
          diffDays 
        });
        
        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays > 1 && diffDays <= 7) return `${diffDays} days ago`;
        
        return date.toLocaleDateString();
      } catch (error) {
        console.error('Error parsing date:', dateString, error);
        return 'Invalid date';
      }
    },
    
    getStatusCount(status) {
      return this.tasks.filter(task => (task.status || 'pending') === status).length;
    },
    
    // Context Pot Methods
    isInContextPot(task) {
      return this.contextPot.some(item => 
        ((item._id && (item._id.$oid || item._id.$id)) || item.id) === ((task._id && (task._id.$oid || task._id.$id)) || task.id)
      );
    },
    
    toggleContextPot(task) {
      this.$emit('add-to-context', task);
    },
    
    // Task completion methods
    isTaskCompleted(task) {
      return task.status === 'completed';
    },
    
    toggleTaskCompletion(task, isChecked) {
      const newStatus = isChecked ? 'completed' : 'pending';
      this.$emit('update-task-status', task, newStatus);
    },
    
    handleTaskCommand(command, task) {
      switch(command) {
        case 'edit':
          this.editTask(task);
          break;
        case 'canvas':
          this.openTaskCanvas(task);
          break;
        case 'delete':
          this.deleteTask(task);
          break;
        case 'delegate':
          this.delegateToAI(task);
          break;
      }
    },
    
    delegateToAI(task) {
      console.log('TaskList: delegateToAI called for task:', task);
      // Emit event to parent to delegate task to AI
      this.$emit('delegate-to-ai', task);
      console.log('TaskList: Event emitted - delegate-to-ai');
    }
  }
};
</script>

<style scoped>
.task-list {
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #fafbfc;
}

.task-list-header {
  padding: 15px;
  border-bottom: 1px solid #ebeef5;
  background-color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.task-list-header h3 {
  margin: 0;
  color: #303133;
  font-size: 16px;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.task-filters {
  padding: 8px 12px;
  background-color: white;
  border-bottom: 1px solid #e1e8ed;
  display: flex;
  align-items: center;
  gap: 8px;
}

.task-list-content {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.no-tasks {
  text-align: center;
  padding: 40px 20px;
  color: #909399;
}

.no-tasks i {
  font-size: 48px;
  margin-bottom: 16px;
  color: #c0c4cc;
}

.no-tasks p {
  margin: 0 0 16px 0;
  font-size: 14px;
}

.task-item {
  background: white;
  border: 1px solid #d1d9e0;
  border-radius: 6px;
  margin-bottom: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  padding: 6px 8px;
}

.task-item:hover {
  border-color: #0969da;
  box-shadow: 0 1px 3px rgba(9, 105, 218, 0.12);
}

.task-item.active {
  border-color: #0969da;
  background-color: #f6f8fa;
  box-shadow: 0 0 0 1px #0969da;
}

.task-item.has-operations {
  border-left: 3px solid #0969da;
}

.task-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.task-checkbox {
  flex-shrink: 0;
  margin-right: 4px;
}

.task-title {
  font-weight: 500;
  color: #24292f;
  font-size: 13px;
  line-height: 1.4;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
  transition: all 0.3s ease;
}

.task-title.completed {
  text-decoration: line-through;
  color: #8b949e;
  opacity: 0.7;
}

.task-status {
  flex-shrink: 0;
}

.operations-count {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 11px;
  color: #0969da;
  flex-shrink: 0;
  font-weight: 500;
}

.operations-count i {
  font-size: 12px;
}

.task-time {
  color: #8b949e;
  font-size: 11px;
  flex-shrink: 0;
  margin-left: auto;
  margin-right: 8px;
}

/* Custom scrollbar */
.task-list-content::-webkit-scrollbar {
  width: 6px;
}

.task-list-content::-webkit-scrollbar-track {
  background: transparent;
}

.task-list-content::-webkit-scrollbar-thumb {
  background: #d1d9e0;
  border-radius: 3px;
}

.task-list-content::-webkit-scrollbar-thumb:hover {
  background: #8b949e;
}

.task-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.task-title-section {
  flex: 1;
  min-width: 0;
}

.task-title {
  font-weight: 600;
  color: #303133;
  font-size: 14px;
  margin-bottom: 4px;
  word-break: break-word;
}

.task-type {
  margin-top: 4px;
}

.task-status-section {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.task-details {
  margin-bottom: 12px;
}

.task-description {
  color: #606266;
  font-size: 13px;
  line-height: 1.4;
  margin-bottom: 8px;
  word-break: break-word;
}

.task-meta {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #909399;
  font-size: 12px;
}

.meta-item i {
  font-size: 14px;
}

.task-operations {
  margin-bottom: 12px;
  padding: 8px 0;
  border-top: 1px solid #f0f0f0;
}

.operations-preview {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.operation-preview {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  background-color: #f8f9fa;
  border-radius: 4px;
  font-size: 12px;
}

.operation-preview i {
  color: #409eff;
  font-size: 14px;
}

.op-status {
  margin-left: auto;
}

.more-operations {
  text-align: center;
  color: #909399;
  font-size: 11px;
  font-style: italic;
  padding: 4px;
}

.task-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 8px;
}

.task-actions .el-button {
  padding: 4px 8px;
  font-size: 12px;
}

.delete-btn {
  color: #f56c6c;
}

.delete-btn:hover {
  color: #f78989;
}

.canvas-btn {
  color: #409eff;
  font-weight: 500;
}

.canvas-btn:hover {
  color: #66b1ff;
  background-color: #ecf5ff;
}

.task-list-footer {
  padding: 12px 15px;
  border-top: 1px solid #ebeef5;
  background-color: #fafafa;
}

.task-stats {
  display: flex;
  justify-content: space-around;
  font-size: 12px;
  color: #606266;
}

.task-stats span {
  padding: 4px 8px;
  background-color: white;
  border-radius: 4px;
  border: 1px solid #e4e7ed;
}

/* Priority indicators */
.priority-low {
  color: #67c23a;
}

.priority-medium {
  color: #e6a23c;
}

.priority-high {
  color: #f56c6c;
}

.priority-urgent {
  color: #f56c6c;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}

/* Status-specific styling */
.task-item .el-tag.pending {
  background-color: #f4f4f5;
  border-color: #d3d4d6;
  color: #909399;
}

.task-item .el-tag.in-progress,
.task-item .el-tag.running {
  background-color: #e6f7ff;
  border-color: #91d5ff;
  color: #1890ff;
}

.task-item .el-tag.completed {
  background-color: #f6ffed;
  border-color: #b7eb8f;
  color: #52c41a;
}

.task-item .el-tag.failed,
.task-item .el-tag.error {
  background-color: #fff2f0;
  border-color: #ffccc7;
  color: #ff4d4f;
}

/* Context Pot Styling */
.task-item {
  position: relative;
}

.task-item.in-context-pot {
  background-color: #f0f9ff;
  border-color: #409eff;
  box-shadow: 0 0 0 1px #409eff;
}

.task-item.in-context-pot::before {
  content: '';
  position: absolute;
  top: -1px;
  left: -1px;
  right: -1px;
  bottom: -1px;
  background: linear-gradient(45deg, #409eff, #66b1ff, #409eff);
  z-index: -1;
  border-radius: 6px;
  animation: contextGlow 2s ease-in-out infinite alternate;
}

@keyframes contextGlow {
  from {
    opacity: 0.5;
  }
  to {
    opacity: 0.8;
  }
}

.task-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.task-item:hover .task-actions {
  opacity: 1;
}

.add-to-context-btn {
  font-size: 11px !important;
  height: 20px !important;
  padding: 0 6px !important;
  border-radius: 10px !important;
  transition: all 0.2s ease;
  display: inline-flex !important;
  align-items: center !important;
  gap: 2px !important;
}

.add-to-context-btn i {
  font-size: 11px !important;
}

.add-to-context-btn .btn-text {
  font-size: 10px;
}

.add-to-context-btn.el-button--success {
  background-color: #67c23a;
  border-color: #67c23a;
}

.add-to-context-btn.el-button--success:hover {
  background-color: #85ce61;
  border-color: #85ce61;
  transform: scale(1.05);
}

.add-to-context-btn.el-button--primary {
  background-color: #409eff;
  border-color: #409eff;
}

.add-to-context-btn.el-button--primary:hover {
  background-color: #66b1ff;
  border-color: #66b1ff;
  transform: scale(1.05);
}

.delegate-ai-btn {
  font-size: 11px !important;
  height: 20px !important;
  padding: 0 6px !important;
  border-radius: 10px !important;
  transition: all 0.2s ease;
  display: inline-flex !important;
  align-items: center !important;
  gap: 2px !important;
  background-color: #e6a23c !important;
  border-color: #e6a23c !important;
  color: white !important;
}

.delegate-ai-btn i {
  font-size: 11px !important;
}

.delegate-ai-btn .btn-text {
  font-size: 10px;
  font-weight: 500;
}

.delegate-ai-btn:hover {
  background-color: #f56c6c !important;
  border-color: #f56c6c !important;
  transform: scale(1.05);
  box-shadow: 0 2px 8px rgba(245, 108, 108, 0.3);
}

.more-actions-btn {
  width: 24px !important;
  height: 20px !important;
  padding: 0 !important;
  border-radius: 4px !important;
  color: #606266 !important;
  transition: all 0.2s ease;
}

.more-actions-btn:hover {
  background-color: #f5f7fa !important;
  color: #409eff !important;
}

.more-actions-btn i {
  font-size: 14px;
  font-weight: bold;
}

/* Dropdown menu styling */
.el-dropdown-menu__item {
  padding: 8px 16px;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.el-dropdown-menu__item i {
  font-size: 14px;
}
</style>